import { Frequency, OccurenceType } from "@vierkant-software/types__api";
import { DateTime, Duration, DurationLike } from "luxon";
import { toDateTime } from "./toDateTime";

export { };

export function toDuration(value: DateTime): Duration {
  if (!value?.isValid) return undefined;
  return Duration.fromObject({hours: value.hour, minutes: value.minute, seconds: value.second});
};

export function between(value: DateTime, a: DateTime, b: DateTime): boolean {
  return a.valueOf() <= value.valueOf() && value.valueOf() <= b.valueOf();
};
export function within([start, end]: [DateTime, DateTime], a: DateTime, b: DateTime): boolean {
  return start.startOf("minute").valueOf() < b.valueOf() && end.startOf("minute").valueOf() > a.valueOf();
}

export function isBefore(ref: DateTime, other: DateTime, inclusive = false): boolean {
  if (!ref?.isValid || !other?.isValid) return false;
  return inclusive ? ref.valueOf() <= other.valueOf() : ref.valueOf() < other.valueOf();
}

export function isAfter(ref: DateTime, other: DateTime, inclusive = false): boolean {
  if (!ref?.isValid || !other?.isValid) return false;
  return inclusive ? ref.valueOf() >= other.valueOf() : ref.valueOf() > other.valueOf();
}

export function generateDateRange(from: DateTime | Date, to: DateTime | Date, step: DurationLike = {days: 1}): DateTime[] {
  from = toDateTime(from);
  to = toDateTime(to);
  if (!from?.isValid || !to?.isValid || !step) return [];
  const dates: DateTime[] = [];
  let current = from.startOf("day");
  while (current <= to) {
    dates.push(current);
    current = current.plus(step);
  }
  return dates;
}

export function isToday(today: DateTime, day: number, month: number, year: number): boolean {
  if (!today.isValid) return false;

  return today.day === day && today.month === month && today.year === year;
}

/**
 * Calculate the amount of days between two DateTimes. May return undefined if the parameter is invalid
 * @param days The dates to calculate the difference of. The first entry should be the start and the last entry be the last of the range.
 * @returns The amount of days between the dateTimes.
 */
export function dayDiff(days: [DateTime, DateTime]) {
  if (!days || days.length !== 2 || days.find(x => !x.isValid)) return undefined;
  const [start, end] = days;
  return Math.ceil(
    end.toUTC().endOf("day").diff(
      start.toUTC().startOf("day")
    ).as("days")
  );
}

declare module "luxon" {
  export interface DateTime {
    toDuration(): Duration;
    between(a: DateTime, b: DateTime): boolean;
    isBefore(other: DateTime, inclusive?: boolean): boolean;
    isAfter(other: DateTime, inclusive?: boolean): boolean;
  }
}

// FIXME: Jens - function is not defined
// maybe because DateTime is already created in msgpack
DateTime.prototype.toDuration = function (this: DateTime) {
  return toDuration(this);
};
DateTime.prototype.between = function (this: DateTime, a: DateTime, b: DateTime) {
  return between(this, a, b);
};

DateTime.prototype.isBefore = function (this: DateTime, other: DateTime, inclusive?: boolean) {
  return isBefore(this, other, inclusive);
};

DateTime.prototype.isAfter = function (this: DateTime, other: DateTime, inclusive?: boolean) {
  return isAfter(this, other, inclusive);
};

export function parseFreq(data: OccurenceType): DurationLike {
  switch(data.freq) {
    case Frequency.SECONDLY: return {seconds: data.interval};
    case Frequency.MINUTELY: return {minutes: data.interval};
    case Frequency.HOURLY:  return {hours: data.interval};
    case Frequency.DAILY:   return {days: data.interval};
    case Frequency.WEEKLY:  return {weeks: data.interval};
    case Frequency.MONTHLY: return {months: data.interval};
    case Frequency.YEARLY:  return {years: data.interval};
    default: return {};
  }
}
